import React, { useEffect, useMemo, useState } from "react";
import StarIcon from "@/assets/icons/PointerStar.svg";
import { useAppSelector } from "@/redux/hooks/hooks";
import { useGetMeQuery } from "@/redux/feature/user/userApi";
import { usePricingFormula } from "@/hooks/usePricingFormula";
import toast from "react-hot-toast";
import PurchaseAiToken from "./components/PurchaseAiToken";
import AiPricingPlans from "./components/AiPricingPlans";
import { cn } from "@/utils/cn";
import {
  useCreateAiTokenPaymentInstanceMutation,
  useInitTopupPurchaseMutation,
  useVerifyAiTokenPaymentMutation,
  useVerifyTopupPurchaseMutation,
} from "@/redux/feature/aiTokenPurchase/aiTokenPurchaseApi";
import { PLANS } from "@/constant/pricingConstant";
import AiTrialCard from "./components/AiTrialCard";

type BaseProps = {
  className?: string;
  children?: React.ReactNode;
};
type RazorpayOrder = {
  id: string;
  currency: "INR" | "USD" | "EUR";
  amount: number; // minor units
};

export const formatPrice = (amount: number, symbol = "$") =>
  `${symbol}${amount}`;

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost";
  onClick?: () => void;
};

const Button: React.FC<ButtonProps> = ({
  className,
  onClick,
  variant = "primary",
  children,
  ...props
}) => {
  const styles = {
    primary:
      "bg-orange-500 text-white hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-300",
    secondary:
      "bg-transparent border border-[#FF6600] text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300",
    ghost:
      "bg-transparent text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300",
  } as const;
  return (
    <button
      onClick={onClick}
      className={cn(
        "inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-medium transition",
        styles[variant],
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
};

type BadgeProps = BaseProps & { variant?: "default" | "subtle" };
const Badge: React.FC<BadgeProps> = ({
  className,
  variant = "subtle",
  children,
}) => (
  <span
    className={cn(
      "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium",
      variant === "subtle"
        ? "bg-gray-100 text-gray-700 border border-gray-200"
        : "bg-gray-800 text-white",
      className
    )}
  >
    {children}
  </span>
);

// ---------------------------------------------------------------------------
// Types & Data
// ---------------------------------------------------------------------------

export type Plan = {
  _id: string;
  id: "starter" | "pro";
  name: string;
  img: string;
  items: string[];
  price: { USD: number; INR: number; EURO: number }; // monthly price in USD
  billing: "per user / mo";
  tagline: string;
  isPopular?: boolean; // show "Popular Choice" badge
  isCurrent?: boolean; // show "Current Plan"
  cta: string; // button label
  tokens: number;
};

export default function AiPricing() {
  const { user } = useAppSelector((s) => s.auth);
  const email = user?.email || "";
  const { data: me } = useGetMeQuery(email, { skip: !email });
  const { ucdT } = usePricingFormula(); // { country, currency, symbols }
  const currency = ucdT?.currency || "USD";
  const symbol = ucdT?.symbols || "$";

  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [loadingError, setLoadingError] = useState(false);
  const [isLoading, setIsLoading] = useState<string | null>(null); // id of in-flight action

  // RTK hooks
  const [initPlanPurchase] = useCreateAiTokenPaymentInstanceMutation();
  const [verifyPlanPurchase] = useVerifyAiTokenPaymentMutation();

  const [initTopupPurchase] = useInitTopupPurchaseMutation(); // ← NEW
  const [verifyTopupPurchase] = useVerifyTopupPurchaseMutation();

  // Load Razorpay
  useEffect(() => {
    const load = (retries = 3) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => {
        setRazorpayLoaded(true);
        setLoadingError(false);
      };
      script.onerror = () => {
        if (retries > 0) {
          setLoadingError(true);
          load(retries - 1);
        } else {
          toast.error(
            "Failed to load payment gateway. Please try again later."
          );
        }
      };
      document.body.appendChild(script);
    };
    load();
    return () => {
      const s = document.querySelector(
        'script[src="https://checkout.razorpay.com/v1/checkout.js"]'
      );
      if (s) document.body.removeChild(s);
    };
  }, []);

  // Helpers
  const openRazorpay = (
    order: any,
    meta: {
      onSuccess: (payload: {
        razorpay_payment_id: string;
        razorpay_order_id: string;
        razorpay_signature: string;
      }) => Promise<void>;
      prefill?: { name?: string; email?: string; contact?: string };
      label: string;
    }
  ) => {
    const options = {
      key: import.meta.env.VITE_RAZORPAY_KEY_ID,
      amount: order.amount,
      currency: order.currency,
      name: "Passionbits",
      order_id: order.id,
      handler: async function (resp: any) {
        try {
          await meta.onSuccess({
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_signature: resp.razorpay_signature,
          });
        } catch (e) {
          console.error(e);
          toast.error("Payment verification failed.");
        } finally {
          setIsLoading(null);
        }
      },
      prefill: meta.prefill,
      theme: { color: "#4066A7" },
      modal: {
        ondismiss: function () {
          setIsLoading(null);
          (window as any).fbq?.("trackCustom", "CheckoutCancelled");
          toast.error("Payment was cancelled");
        },
      },
    };
    const rzp = new (window as any).Razorpay!(options);
    rzp.open();
  };

  const canPay = useMemo(() => {
    if (!email) return "Please sign in first.";
    if (!razorpayLoaded) return "Payment gateway not loaded yet.";
    if (loadingError) return "Payment gateway failed to load.";
    return null;
  }, [email, razorpayLoaded, loadingError]);

  // ---- PLAN PURCHASE FLOW ----
  const onPlanCTA = async (plan: (typeof PLANS)[number]) => {
    console.log("here");
    console.log(plan);
    if (canPay) return toast.error(canPay);
    setIsLoading(`plan_${plan.id}`);

    try {
      // 1) Init on backend — it chooses correct currency/amount from country code
      const initRes: any = await initPlanPurchase({
        planId: plan._id ?? plan.id, // if you pass mongo id from PLANS, prefer _id
        userEmail: email,
      }).unwrap();

      const { order, plan: planSnapshot } = initRes?.data; // order: { id, amount, currency }
      openRazorpay(order as RazorpayOrder, {
        label: `Buy ${plan.name}`,
        prefill: {
          name: me?.data?.fullName,
          email: me?.data?.email,
          contact: me?.data?.phone,
        },
        onSuccess: async (rp) => {
          // 2) Verify on backend
          const verifyRes: any = await verifyPlanPurchase({
            userEmail: email,
            userId: me?.data?._id, // mongo id
            planId: planSnapshot?._id ?? plan._id ?? plan.id,
            razorpay_order_id: rp.razorpay_order_id,
            razorpay_payment_id: rp.razorpay_payment_id,
            razorpay_signature: rp.razorpay_signature,
          }).unwrap();

          toast.success("Plan purchased successfully!");
          (window as any).fbq?.("track", "Purchase", {
            value: (order.amount / 100).toFixed(2),
            currency: order.currency || currency,
            content_name: plan.name,
            content_type: "subscription",
          });
        },
      });
    } catch (err: any) {
      console.error(err);
      toast.error(err?.data?.message || "Could not initialize purchase.");
      setIsLoading(null);
    }
  };

  // Enable the top-up flow (uncomment & slightly tweak)
  const onTopupPurchase = async () => {
    if (canPay) return toast.error(canPay);
    setIsLoading("topup_10");

    try {
      const initRes: any = await initTopupPurchase({
        packId: "topup_10",
        userEmail: email,
      }).unwrap();

      const { order, pack } = initRes?.data;
      openRazorpay(order as RazorpayOrder, {
        label: "Buy 10 Tokens",
        prefill: {
          name: me?.data?.fullName,
          email: me?.data?.email,
          contact: me?.data?.phone,
        },
        onSuccess: async (rp) => {
          await verifyTopupPurchase({
            userEmail: email,
            razorpay_order_id: rp.razorpay_order_id,
            razorpay_payment_id: rp.razorpay_payment_id,
            razorpay_signature: rp.razorpay_signature,
            packId: pack?.id ?? "topup_10",
          }).unwrap();

          toast.success("Tokens added to your balance!");
          (window as any).fbq?.("track", "Purchase", {
            value: (order.amount / 100).toFixed(2),
            currency: order.currency || currency,
            content_name: "AI Token Top-up (10)",
            content_type: "consumable",
          });
        },
      });
    } catch (err: any) {
      console.error(err);
      toast.error(err?.data?.message || "Could not initialize top-up.");
      setIsLoading(null);
    }
  };

  return (
    <section className="mx-auto max-w-5xl px-4 py-8 sm:py-10">
      {/* Header */}
      <header className="mb-6 text-center sm:mb-8">
        <h1 className="text-[32px] font-medium tracking-tight">
          Plan & Tokens
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Everything about your subscription and top-ups—at a glance.
        </p>
      </header>

      <div className="space-y-6">
        {/* 🚀 New Free Trial Card */}
        <AiTrialCard />
        <PurchaseAiToken
          onTopupPurchase={onTopupPurchase}
          isLoading={isLoading as string}
          symbol={symbol}
        />

        <AiPricingPlans
          isLoading={isLoading as string}
          symbol={symbol}
          onPlanCTA={onPlanCTA}
        />
      </div>
    </section>
  );
}
